import type { PostMediaItem } from '@ps/api/types';

/**
 * Picks ONE aspect ratio (width / height) for the whole
 * carousel so every slide in a post is the same size — 1:1
 * with legacy `calculateOptimalHeight` in
 * `flows/bio/wall/components/post/slide-view.tsx:537-604`.
 *
 * Strategy: weighted average of every media item's intrinsic
 * `width / height`, weighted by area so a big landscape image
 * has more influence than a tiny portrait icon. The result
 * is clamped to `[minAspectRatio, maxAspectRatio]` so a
 * single extreme portrait/landscape doesn't blow up the
 * card. Legacy bounds: 0.9 (slightly taller than square) to
 * 1.25 (5:4, slightly wider than square).
 *
 * When no media reports dimensions (e.g. legacy posts saved
 * before the backend started persisting `metadata.dimensions`),
 * falls back to `fallbackAspectRatio` (caller passes 1 for
 * the carousel use case — same default the legacy renderer
 * uses via `defaultMediaAspectRatio ?? defaultAspectRatio ?? 1`).
 *
 * Returns the aspect ratio number itself (not a px height) —
 * CSS does the height math via `aspectRatio: 'W / H'` so the
 * carousel responds to the parent width without JS.
 */
export function calculatePostMediaAspectRatio(
  medias: PostMediaItem[],
  fallbackAspectRatio = 1,
  minAspectRatio = 0.9,
  maxAspectRatio = 5 / 4,
): number {
  const fallback = Math.max(fallbackAspectRatio, minAspectRatio);

  const dimensioned = medias
    .map(m => m.metadata?.dimensions)
    .filter(
      (d): d is { width: number; height: number } =>
        d != null &&
        typeof d.width === 'number' &&
        typeof d.height === 'number' &&
        d.width > 0 &&
        d.height > 0,
    );

  if (dimensioned.length === 0) return fallback;

  // Weighted by area — bigger media pulls the shared ratio
  // toward its own. Mirrors the `'weighted'` branch of
  // legacy `calculateOptimalHeight` (the wall passes that
  // strategy at `slide-view.tsx:90`).
  const totalArea = dimensioned.reduce((sum, d) => sum + d.width * d.height, 0);
  let target = dimensioned.reduce((sum, d) => {
    const weight = (d.width * d.height) / totalArea;
    return sum + (d.width / d.height) * weight;
  }, 0);

  if (target < minAspectRatio) target = minAspectRatio;
  if (target > maxAspectRatio) target = maxAspectRatio;
  return target;
}
